import { isOfflineDbAvailable } from './offline-db';
import { isNetworkError } from './outbox-errors';
import { outboxStore } from './outbox-store';
import { newIdempotencyKey, submissionHeaders, type SubmissionHeaders } from './submission-headers';
import type { OutboxItem, OutboxKind, OutboxPayloads } from './outbox.types';

export type SubmitOrQueueResult<T> = { queued: false; data: T } | { queued: true; item: OutboxItem };

/** The contribution could not be kept on this device (storage full, private mode). */
export class OfflineQueueError extends Error {
  constructor(readonly reason: unknown) {
    super('OFFLINE_STORAGE_UNAVAILABLE');
    this.name = 'OfflineQueueError';
  }
}

export const isOfflineQueueError = (error: unknown): error is OfflineQueueError => error instanceof OfflineQueueError;

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Sends a contribution now, or keeps it on the device when there is no
 * network. The idempotency key and the recorded-at date are fixed here, before
 * the first try: if the answer of that try is lost, the resend from the queue
 * carries the same key and the server does not record it twice.
 *
 * Only a missing answer queues the contribution. An HTTP error (validation,
 * cross-validation gate, signed out) is thrown as before for the page to handle.
 */
export async function submitOrQueue<K extends OutboxKind, T>({
  ownerId,
  kind,
  payload,
  summary,
  send,
}: {
  ownerId: string;
  kind: K;
  payload: OutboxPayloads[K];
  summary: { lang: string; text: string };
  send: (headers: SubmissionHeaders) => Promise<T>;
}): Promise<SubmitOrQueueResult<T>> {
  const idempotencyKey = newIdempotencyKey();
  const createdAt = new Date().toISOString();

  const queue = async (): Promise<SubmitOrQueueResult<T>> => {
    try {
      const item = await outboxStore.enqueue({ ownerId, kind, payload, summary, idempotencyKey, createdAt });
      return { queued: true, item };
    } catch (error) {
      throw new OfflineQueueError(error);
    }
  };

  const canQueue = isOfflineDbAvailable();
  if (canQueue && isOffline()) return queue();

  try {
    return { queued: false, data: await send(submissionHeaders(idempotencyKey, createdAt)) };
  } catch (error) {
    if (canQueue && isNetworkError(error)) return queue();
    throw error;
  }
}
